import React from "react";
import { Row, Col } from "react-bootstrap";
import { RBSModal } from "../../base/rbs_components/RBSModal.tsx";
import { RBSTextField } from "../../base/rbs_components/RBSTextField.tsx";
import { RBSDropdown } from "../../base/rbs_components/RBSDropdown.tsx";
import type {
    CashTrancheInput,
    CashTranche,
    WaterfallItemInput,
    WaterfallItem,
    TimeSeries,
    Variable
} from "../../base/ui_general/GeneralInterfaces.tsx";
import {
    DefaultDefinitionEnum, WaterfallItemTypeEnum, LabelTextMessageTypes, ValidationSchemes, TextfieldInputTypes,
    DisplayFormatTypeEnum
} from "../../base/Settings.tsx";
import { getLabelTextMessage } from "../../base/utils/GeneralUtilities.tsx";
import type { InputStateControl } from "../../base/utils/InputStateControl.tsx";
import type { UserLanguages } from "../../base/Settings.tsx";

/**
 * Props for CashTrancheDetailModal.
 * This interface describes the data structure for displaying and editing a Cash Tranche in a modal dialog.
 */
interface CashTrancheDetailModalProps {
    /** Indicates whether the modal is visible. */
    show: boolean;
    /** The CashTranche to display or edit (main type, not input). */
    cashTranche?: CashTranche;
    /** The associated WaterfallItem to display or edit. */
    waterfall_item?: WaterfallItem;
    /** Callback to submit the cash tranche input. */
    onSubmit: (cashTrancheInput: CashTrancheInput, waterfallItemInput: WaterfallItemInput) => void;
    /** Callback to close the modal. */
    onClose: () => void;
    /** Indicates if the form input is valid. */
    isValid: boolean;
    /** InputStateControl instance for managing input state and validation. */
    inputStateControl: InputStateControl;
    /** Indicates the preferred user language. */
    user_language: UserLanguages;
    /** Array of available time series for base_curve_id selection. */
    time_series: Array<TimeSeries> | undefined;
    /** Array of available variables for target_amortization_variable_id selection. */
    variables: Array<Variable> | undefined;
}

export const CashTrancheDetailModal: React.FC<CashTrancheDetailModalProps> = (props: CashTrancheDetailModalProps) => {
    // Helper to prefill values from cashTranche or waterfall_item, or use defaults
    const prefill = (key: string): string => {
        if (props.cashTranche && key in props.cashTranche) {
            const value = props.cashTranche[key as keyof typeof props.cashTranche];
            if (key === "invested") return value ? "true" : "false";
            return String(value ?? "");
        }
        if (props.waterfall_item && key in props.waterfall_item) {
            const value = props.waterfall_item[key as keyof typeof props.waterfall_item];
            if (key === "invested") return value ? "true" : "false";
            return String(value ?? "");
        }
        switch (key) {
            case "base_curve_id":
                return "";
            case "name":
                return "New Cash Tranche";
            case "description":
                return "Cash tranche for transaction";
            case "initial_nominal":
                return "5000000";
            case "spread":
                return "0.02";
            case "default_definition":
                return String(DefaultDefinitionEnum.DEFERRABLE);
            case "invested":
                return "true";
            case "target_return_on_equity":
                return "0.125";
            case  "target_amortization_variable_id":
                return "";
            default:
                return "";
        }
    };
    const headerTitle = props.cashTranche
        ? getLabelTextMessage(LabelTextMessageTypes.UPDATE_CASH_TRANCHE_TITLE, props.user_language)
        : getLabelTextMessage(LabelTextMessageTypes.NEW_CASH_TRANCHE_CREATION_TITLE, props.user_language);
    return (
        <RBSModal
            show={props.show}
            header_title={headerTitle}
            show_close_button
            onClose={props.onClose}
            primary_button_label={getLabelTextMessage(LabelTextMessageTypes.SUBMIT, props.user_language)}
            secondary_button_label={getLabelTextMessage(LabelTextMessageTypes.CANCEL, props.user_language)}
            onClickSecondary={props.onClose}
            primary_button_disabled={!props.isValid}
            size={"xl"}
            onClickPrimary={() => {
                const investedStr = props.inputStateControl.getStringValueForKey("invested");
                const baseCurveIdRaw = props.inputStateControl.getStringValueForKey("base_curve_id");
                const targetAmortizationVariableRaw = props.inputStateControl.getStringValueForKey(
                    "target_amortization_variable_id");
                const defaultDefinitionRaw = props.inputStateControl.getStringValueForKey("default_definition");
                let transactionIdRaw = props.inputStateControl.getStringValueForKey("transaction_id");
                if (!transactionIdRaw) transactionIdRaw = "";
                const cashTrancheInput: CashTrancheInput = {
                    initial_nominal: Number(props.inputStateControl.getStringValueForKey("initial_nominal")),
                    spread: Number(props.inputStateControl.getStringValueForKey("spread")),
                    base_curve_id: baseCurveIdRaw === "" ? undefined : baseCurveIdRaw,
                    default_definition: Object.values(DefaultDefinitionEnum).includes(defaultDefinitionRaw as DefaultDefinitionEnum)
                        ? (defaultDefinitionRaw as DefaultDefinitionEnum)
                        : DefaultDefinitionEnum.DEFERRABLE,
                    invested: investedStr === "true",
                    target_return_on_equity: Number(props.inputStateControl.getStringValueForKey("target_return_on_equity")),
                    target_amortization_variable_id: targetAmortizationVariableRaw === "" ? undefined : targetAmortizationVariableRaw
                };
                const waterfallItemInput: WaterfallItemInput = {
                    name: props.inputStateControl.getStringValueForKey("name") ?? "",
                    description: props.inputStateControl.getStringValueForKey("description") ?? "",
                    transaction_id: transactionIdRaw,
                    type: WaterfallItemTypeEnum.CASH_TRANCHE,
                };
                props.onSubmit(cashTrancheInput, waterfallItemInput);
                props.onClose();
            }}
        >
            <Row>
                <Col md={6}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.NAME, props.user_language)}
                        input_key="name"
                        prefill={prefill("name")}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.TEXT}
                    />
                </Col>
                <Col md={6}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.INITIAL_NOMINAL, props.user_language)}
                        input_key="initial_nominal"
                        prefill={prefill("initial_nominal")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER, ValidationSchemes.NON_NEGATIVE]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.SPREAD, props.user_language)}
                        input_key="spread"
                        prefill={prefill("spread")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
                <Col md={4}>
                    <RBSDropdown
                        label={getLabelTextMessage(LabelTextMessageTypes.BASE_CURVE_ID, props.user_language)}
                        input_key="base_curve_id"
                        prefill={prefill("base_curve_id")}
                        items={[{ label: "---", value: "" }, ...(props.time_series ? props.time_series.map(ts => ({ label: ts.name, value: ts.id })) : [])]}
                        input_state_control={props.inputStateControl}
                        validation_schemes={[]}
                    />
                </Col>
                <Col md={4}>
                    <RBSDropdown
                        label={getLabelTextMessage(LabelTextMessageTypes.DEFAULT_DEFINITION, props.user_language)}
                        input_key="default_definition"
                        prefill={prefill("default_definition")}
                        items={Object.values(DefaultDefinitionEnum).map(dd => ({ label: dd, value: dd }))}
                        input_state_control={props.inputStateControl}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <RBSDropdown
                        label={getLabelTextMessage(LabelTextMessageTypes.INVESTED, props.user_language)}
                        input_key="invested"
                        prefill={prefill("invested")}
                        items={[{ label: "Yes", value: "true" }, { label: "No", value: "false" }]}
                        input_state_control={props.inputStateControl}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                    />
                </Col>
                <Col md={4}>
                    <RBSDropdown
                        label={getLabelTextMessage(LabelTextMessageTypes.TARGET_AMORTIZATION_VARIABLE_ID, props.user_language)}
                        input_key="target_amortization_variable_id"
                        prefill={prefill("target_amortization_variable_id")}
                        items={[{ label: "---", value: "" }, ...(props.variables ? props.variables.filter(
                            v => v.display_format === DisplayFormatTypeEnum.AMOUNT).sort(
                                (a, b) => a.name.localeCompare(b.name)).map(
                                v => ({ label: v.name, value: v.id })) : [])]}
                        input_state_control={props.inputStateControl}
                        validation_schemes={[]}
                    />
                </Col>
                <Col md={4}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.TARGET_RETURN_ON_EQUITY, props.user_language)}
                        input_key="target_return_on_equity"
                        prefill={prefill("target_return_on_equity")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER, ValidationSchemes.NON_NEGATIVE]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.DESCRIPTION, props.user_language)}
                        input_key="description"
                        prefill={prefill("description")}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.TEXT}
                        multiline={true}
                        style={{ minHeight: 300 }}
                    />
                </Col>
            </Row>
        </RBSModal>
    );
};
